import { isAfter, isBefore } from "date-fns";

import { PdfIcon } from "@/assets/icons";
import { StatusColorType } from "@/components/atoms/Status/styled";
import { FeatureItemProps, InfoItemProps } from "@/components/organisms";
import { defaultDateFormat, formatDueDateDDMM } from "@/core/date";
import { defaultCurrencyFormat, defaultDecimalFormat, trailZerosAfterDecimal } from "@/core/number";
import i18n from "@/i18n";
import { ContractDetail } from "@/shared/domain";

export const getCoverageStatus = (riskObject: MyBrokerApi.ContractDetailObject, index: number): InfoItemProps[] => {
    const riskObjectSchema = [] as InfoItemProps[];

    const hasEndDate = !!riskObject.endDate;
    const hasStartDate = !!riskObject.startDate;
    const formattedStartDate = riskObject.startDate && defaultDateFormat(riskObject.startDate);
    const formattedEndDate = riskObject.endDate && defaultDateFormat(riskObject.endDate);

    if (hasEndDate && isBefore(riskObject.endDate, new Date())) {
        // expired since ...
        riskObjectSchema.push({
            isStatus: true,
            color: StatusColorType.Error,
            key: `endDate${index}`,
            value: i18n.t("Coverage.ExpiredSince", { date: formattedEndDate }),
        });
    } else if (hasEndDate && hasStartDate) {
        // covered from ... until ...
        riskObjectSchema.push({
            isStatus: true,
            color: StatusColorType.Info,
            key: `endDate${index}`,
            value: i18n.t("Coverage.CoverageFromUntil", { startDate: formattedStartDate, endDate: formattedEndDate }),
        });
    } else if (hasEndDate) {
        // covered until ...
        riskObjectSchema.push({
            isStatus: true,
            color: StatusColorType.Info,
            key: `endDate${index}`,
            value: i18n.t("Coverage.CoverageEndsOn", { date: formattedEndDate }),
        });
    } else if (hasStartDate && isAfter(new Date(riskObject.startDate), new Date())) {
        // coverage starts on ...
        riskObjectSchema.push({
            isStatus: true,
            color: StatusColorType.Warning,
            key: `startDate${index}`,
            value: i18n.t("Coverage.CoverageStartOn", { date: formattedStartDate }),
        });
    }
    return riskObjectSchema;
};

export const panelRiskObjectSchema = (
    riskObject: MyBrokerApi.ContractDetailObject,
    language: string,
    index: number,
): InfoItemProps[] => {
    let riskObjectSchema = getCoverageStatus(riskObject, index);

    riskObjectSchema.push({
        key: `description${index}`,
        label: riskObject.objectType?.name,
        value: riskObject.description,
    });

    // Fire - Capital

    const existFireCapital =
        riskObject.extraFireCapitalIndex || riskObject.extraFireCapitalIndexNow || riskObject.extraFireGridResult;

    if (existFireCapital) {
        riskObjectSchema = [
            ...riskObjectSchema,
            {
                key: `capitalFire${index}`,
                label: i18n.t("Common.Capital"),
                features: [],
            },
            {
                key: `extraFireCapitalIndex${index}`,
                label: i18n.t("Common.CapitalIndex"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.extraFireCapitalIndex, language),
            },
            {
                key: `extraFireCapitalIndexNow${index}`,
                label: i18n.t("Common.CurrentIndex"),
                hideIfZero: true,
                value: defaultDecimalFormat(riskObject.extraFireCapitalIndexNow, language, "", 2),
            },
            {
                key: `extraFireGridResult${index}`,
                label: i18n.t("Common.GridResult"),
                hideIfZero: true,
                value: riskObject.extraFireGridResult,
            },
            {
                key: `extraFireGridType${index}`,
                label: i18n.t("Common.GridType"),
                hideIfZero: true,
                value: riskObject.extraFireGridResult && riskObject.extraFireGridType.name,
            },
        ];
    }

    // Fire - Company damages

    const existFireCompanyDamages =
        riskObject.extraFireRentMonth ||
        riskObject.extraFireRentType?.name ||
        riskObject.extraFireRentAmount ||
        riskObject.extraFireRentIndexType?.name;

    if (existFireCompanyDamages) {
        riskObjectSchema = [
            ...riskObjectSchema,
            {
                key: `companyDamages${index}`,
                label: i18n.t("Common.CompanyDamages"),
                features: [],
            },
            {
                key: `extraFireRentMonth${index}`,
                label: i18n.t("Common.PayoutMonths"),
                hideIfZero: true,
                value: riskObject.extraFireRentMonth,
            },
            {
                key: `extraFireRentType${index}`,
                label: i18n.t("Common.PayoutType"),
                value: riskObject.extraFireRentType?.name,
            },
            {
                key: `extraFireRentAmount${index}`,
                label: i18n.t("Common.PayableAmount"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.extraFireRentAmount, language),
            },
            {
                key: `extraFireRentIndexType${index}`,
                label: i18n.t("Common.IndexType"),
                value: riskObject.extraFireRentIndexType?.name,
            },
        ];
    }

    // Fire - Theft

    const existFireTheft =
        riskObject.extraFireTheftCoverage?.name ||
        (riskObject.extraFirePercentInsuredValue &&
            trailZerosAfterDecimal(riskObject.extraFirePercentInsuredValue) !== 0) ||
        (riskObject.extraFireFirstRisk && trailZerosAfterDecimal(riskObject.extraFireFirstRisk) !== 0);

    if (existFireTheft) {
        riskObjectSchema = [
            ...riskObjectSchema,
            {
                key: `theft${index}`,
                label: i18n.t("Common.Theft"),
                features: [],
            },
            {
                key: `extraFireTheftCoverage${index}`,
                label: i18n.t("Common.TheftCoverage"),
                value: riskObject.extraFireTheftCoverage?.name,
            },
            {
                key: `extraFirePercentInsuredValue${index}`,
                label: i18n.t("Common.PercentInsuredValue"),
                hideIfZero: true,
                value: riskObject.extraFirePercentInsuredValue,
            },
            {
                key: `extraFireFirstRisk${index}`,
                label: i18n.t("Common.PrimaryRisk"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.extraFireFirstRisk, language, true),
            },
        ];
    }

    // Vehicle

    riskObjectSchema = [
        ...riskObjectSchema,
        {
            key: `depreciationType${index}`,
            label: i18n.t("Common.DepreciationType"),
            value: riskObject.extraCarValueDamage?.name,
        },
        {
            tooltip: i18n.t("InsuredGood.Details.InsuredValueDoesNotRepresentTheValueOfTheCompensation"),
            key: `insuredValue${index}`,
            label: i18n.t("Common.InsuredValue"),
            hideIfZero: true,
            value: defaultCurrencyFormat(riskObject.extraCarValueInsured, language),
        },
        {
            key: `roadTax${index}`,
            label: i18n.t("Common.RoadTax"),
            hideIfZero: true,
            value: defaultCurrencyFormat(riskObject.extraCarLicenseTax, language),
        },
    ];

    // Life

    const existLife =
        riskObject.extraLifeLifeCapital ||
        riskObject.extraLifeDeathCapital ||
        riskObject.extraLifeDateLastProblem ||
        riskObject.extraLifeFiscalType?.name;

    if (existLife) {
        riskObjectSchema = [
            ...riskObjectSchema,
            {
                key: `capitalLife${index}`,
                label: i18n.t("Common.Capital"),
                features: [],
            },
            {
                key: `extraLifeLifeCapital${index}`,
                label: i18n.t("Common.LifeCapital"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.extraLifeLifeCapital, language),
            },
            {
                key: `extraLifeDeathCapital${index}`,
                label: i18n.t("Common.DeathCapital"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.extraLifeDeathCapital, language),
            },
            {
                key: `extraLifeDateLastProblem${index}`,
                label: i18n.t("Common.DateLastProblem"),
                value: defaultDateFormat(riskObject.extraLifeDateLastProblem),
            },
            {
                key: `extraLifeFiscalType${index}`,
                label: i18n.t("Common.FiscalType"),
                value: riskObject.extraLifeFiscalType?.name,
            },
        ];
    }

    // Loan

    const existLoan = !!riskObject.listLoan[0];

    if (existLoan) {
        riskObjectSchema = [
            ...riskObjectSchema,
            {
                key: `loan${index}`,
                label: i18n.t("Common.Loan"),
                features: [],
            },
            {
                key: `reference${index}`,
                label: i18n.t("Common.Reference"),
                value: riskObject.listLoan[0].reference,
            },
            {
                key: `loanType${index}`,
                label: i18n.t("Common.LoanType"),
                value: riskObject.listLoan[0].loanType?.name,
            },
            {
                key: `refundType${index}`,
                label: i18n.t("Common.RefundType"),
                value: riskObject.listLoan[0].refundType?.name,
            },
            {
                key: `revisionType${index}`,
                label: i18n.t("Common.RevisionType"),
                value: riskObject.listLoan[0].revisionType?.name,
            },
            {
                key: `formula${index}`,
                label: i18n.t("Common.Formula"),
                value: riskObject.listLoan[0].formula,
            },
            {
                key: `goal${index}`,
                label: i18n.t("Common.Goal"),
                value: riskObject.listLoan[0].goal?.name,
            },
            {
                key: `effectiveDate${index}`,
                label: i18n.t("Common.DateAct"),
                value: defaultDateFormat(riskObject.listLoan[0]?.effectiveDate),
            },
            {
                key: `expirationDate${index}`,
                label: i18n.t("Common.ExpirationDate"),
                value: defaultDateFormat(riskObject.listLoan[0]?.expirationDate),
            },
            {
                key: `duration${index}`,
                label: i18n.t("Common.DurationInMonths"),
                value: riskObject.listLoan[0].durationInMonths,
            },
            {
                key: `amount${index}`,
                label: i18n.t("Common.Amount"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.listLoan[0].amount, language),
            },
            {
                key: `monthlyPayment${index}`,
                label: i18n.t("Common.MonthlyPayment"),
                hideIfZero: true,
                value: defaultCurrencyFormat(riskObject.listLoan[0].monthlyPayment, language),
            },
            {
                key: `interestRate${index}`,
                label: i18n.t("Common.InterestRate"),
                hideIfZero: true,
                value: riskObject.listLoan[0].interestRate && `${riskObject.listLoan[0].interestRate}%`,
            },
            {
                key: `interestRateFrequency${index}`,
                label: i18n.t("Common.InterestRateFrequency"),
                value: riskObject.listLoan[0].interestRateFrequency?.name,
            },
        ];
    }

    // Invest Fund

    const existFund = riskObject.listFunds && riskObject.listFunds.length > 0;

    if (existFund) {
        riskObjectSchema.push({
            key: `funds${index}`,
            label: i18n.t("Common.Funds"),
            features: [],
        });

        riskObject.listFunds.forEach((fund, i) => {
            riskObjectSchema.push({
                key: `fundName${index}_${i}`,
                label: i18n.t("Common.FundName"),
                value: fund.fundName,
            });
            riskObjectSchema.push({
                key: `currency${index}_${i}`,
                label: i18n.t("Common.Currency"),
                value: fund.currency,
            });
        });
    }

    // Warranties
    riskObjectSchema = [
        ...riskObjectSchema,
        {
            key: `warranties${index}`,
            label: i18n.t("Common.WarrantiesApplied"),
            features: riskObject.listWarranty.map((warranty) => {
                const mappedWarranty: FeatureItemProps = {
                    key: `warranty${warranty.guarantee.id}`,
                    label: warranty.guarantee.name,
                    infos: [],
                };

                mappedWarranty.infos.pushIf(
                    "warrantyStartDate" in warranty && isAfter(new Date(warranty.warrantyStartDate), new Date()),
                    {
                        key: `warrantyStartDate${index}`,
                        label: i18n.t("Common.StartDate"),
                        value: defaultDateFormat(warranty.warrantyStartDate),
                    },
                );

                mappedWarranty.infos.push({
                    key: `warrantyEndDate${index}`,
                    label: i18n.t("Common.EndDate"),
                    value: defaultDateFormat(warranty.warrantyEndDate),
                });

                if (warranty.guarantee.id === "121") {
                    mappedWarranty.infos.pushIf(riskObject.extraLifeAnnualTax, {
                        key: `extraLifeAnnualTax${index}`,
                        label: i18n.t("Common.AnnualTax"),
                        value: `${riskObject.extraLifeAnnualTax}%`,
                    });
                    mappedWarranty.infos.pushIf(riskObject.extraLifeBalanceType?.name, {
                        key: `extraLifeBalanceType${index}`,
                        label: i18n.t("Common.BalanceType"),
                        value: riskObject.extraLifeBalanceType?.name,
                    });
                }
                mappedWarranty.infos.pushIf(!!warranty.franchiseValue && warranty.franchiseValue !== "0.00", {
                    key: `franchiseValue${index}`,
                    label: i18n.t("Common.Franchise"),
                    value: warranty.franchiseValue,
                });

                mappedWarranty.infos.pushIf(warranty.formula, {
                    key: `formula${index}`,
                    label: i18n.t("Common.Formula"),
                    value: warranty.formula,
                });

                return mappedWarranty;
            }),
        },
    ];

    return riskObjectSchema;
};

export const panelPaymentSchema = (contractDetails: ContractDetail, language: string): InfoItemProps[] => {
    return [
        {
            key: "periodicity",
            label: i18n.t("Common.Periodicity"),
            value: contractDetails.periodicity?.name,
        },
        {
            key: "lastTotalAmount",
            label: i18n.t("Common.Amount"),
            hideIfZero: true,
            value: defaultCurrencyFormat(contractDetails.lastTotalAmount, language),
        },
        {
            key: "dueDateDDMM",
            label: i18n.t("Common.PaymentDate"),
            value: contractDetails.dueDateDDMM && formatDueDateDDMM(contractDetails.dueDateDDMM),
        },
    ];
};

export const panelGeneralConditionsSchema = (
    contractDetails: ContractDetail,
    onReferenceClick: (reference: string) => void,
): InfoItemProps[] => {
    return contractDetails.listContractClause.map((contractClause, i) => {
        if (!contractClause.hasClauseRef && contractClause.url) {
            return {
                key: `contractClause${i}`,
                label: i18n.t("Common.Url"),
                isLink: true,
                value: `<a href="${contractClause.url}" target="_blank">${contractClause.url}</a>`,
            };
        }
        return {
            key: `contractClause${i}`,
            label: i18n.t("Common.Reference"),
            isLink: true,
            IconComponent: PdfIcon,
            value: contractClause.clauseReference && `<span class="link">${contractClause.clauseReference}</span>`,
            onClick: () => onReferenceClick(contractClause.clauseReference),
        };
    });
};

export const panelManagerSchema = (contractDetails: ContractDetail): InfoItemProps[] => [
    {
        key: "name",
        label: i18n.t("Common.Name"),
        value: contractDetails.managerName,
    },
    {
        key: "phone",
        label: i18n.t("Common.Phone"),
        value: contractDetails.managerPhone,
    },
    {
        key: "email",
        label: i18n.t("Common.Email"),
        value: contractDetails.managerEmail,
    },
];

export const panelSalesRepresentativeSchema = (contractDetails: ContractDetail): InfoItemProps[] => [
    {
        key: "name",
        label: i18n.t("Common.Name"),
        value: contractDetails.salesName,
    },
];
